import { create } from 'zustand';
import { Perfil } from "../model/Usuario/Perfil";
import { Permissao } from "../model/Usuario/Permissao";
import Usuario from "../model/Usuario/Usuario";
import { extractNameFromToken } from '../utils/jwtUtils';

interface UserStore {
  usuario: Usuario | null;
  perfis: Perfil[];
  permissoes: Permissao[];
  setUsuario: (usuario: Usuario | null) => void;
  login: (usuario: Usuario) => void;
  logout: () => void;
  isLoggedIn: () => boolean;
  updateUsuarioInfo: (dadosAtualizados: Partial<Usuario>) => void;
  updateNameFromToken: (token: string) => void;
  addPerfil: (perfil: Perfil) => void;
  removePerfil: (perfilId: string) => void;
  updatePerfil: (perfilId: string, dadosAtualizados: Partial<Perfil>) => void;
  addPermissao: (permissao: Permissao) => void;
  removePermissao: (permissaoId: string) => void;
  updatePermissao: (permissaoId: string, dadosAtualizados: Partial<Permissao>) => void;
}

// Estado inicial
const useUserStore = create<UserStore>((set, get) => ({
  usuario: null,
  perfis: [],
  permissoes: [],

  setUsuario: (usuario: Usuario | null) => set({ usuario }),

  login: (usuario: Usuario) => set({ usuario }),

  logout: () => set({ usuario: null }),

  isLoggedIn: () => !!get().usuario,

  updateUsuarioInfo: (dadosAtualizados: Partial<Usuario>) =>
    set((state) => ({
      usuario: state.usuario ? { ...state.usuario, ...dadosAtualizados } : null,
    })),

  updateNameFromToken: (token: string) => {
    const name = extractNameFromToken(token);
    if (name) {
      set((state) => ({
        usuario: state.usuario ? { ...state.usuario, nome: name } : null,
      }));
    }
  },

  addPerfil: (perfil: Perfil) => set((state) => ({
    perfis: [...state.perfis, perfil],
  })),

  removePerfil: (perfilId: string) => set((state) => ({
    perfis: state.perfis.filter((perfil) => perfil.id !== perfilId),
  })),

  updatePerfil: (perfilId: string, dadosAtualizados: Partial<Perfil>) => set((state) => ({
    perfis: state.perfis.map((perfil) =>
      perfil.id === perfilId ? { ...perfil, ...dadosAtualizados } : perfil
    ),
  })),

  addPermissao: (permissao: Permissao) => set((state) => ({
    permissoes: [...state.permissoes, permissao],
  })),

  removePermissao: (permissaoId: string) => set((state) => ({
    permissoes: state.permissoes.filter((permissao) => permissao.id !== permissaoId),
  })),

  updatePermissao: (permissaoId: string, dadosAtualizados: Partial<Permissao>) => set((state) => ({
    permissoes: state.permissoes.map((permissao) =>
      permissao.id === permissaoId ? { ...permissao, ...dadosAtualizados } : permissao
    ),
  })),
}));

export default useUserStore;
